import {
  TIMELINE_DOMAIN_OPTIONS,
  TIMELINE_SOURCE_OPTIONS,
  TIMELINE_TEST_DATA_OPTIONS,
} from "@/lib/activityTimeline/types";
import type { ActivityTimelineFilterState } from "@/lib/activityTimeline/useActivityTimeline";
import type { OrgPickRow } from "./devPanelTypes";

const selectClass =
  "rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";
const inputClass =
  "rounded-lg border border-gray-300 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100";

export function DevActivityForensicsFilters(props: {
  organizationId: string;
  onOrganizationId: (id: string) => void;
  orgs: OrgPickRow[];
  orgsLoading: boolean;
  filters: ActivityTimelineFilterState;
  patchFilters: (patch: Partial<ActivityTimelineFilterState>) => void;
  working: boolean;
  onRefresh: () => void;
  onReset: () => void;
}) {
  const { filters, patchFilters } = props;
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="min-w-[12rem]">
        <label className="mb-1 block text-xs text-gray-500">Organization</label>
        <select
          className={`${selectClass} w-full`}
          value={props.organizationId}
          onChange={(e) => props.onOrganizationId(e.target.value)}
          disabled={props.orgsLoading || props.orgs.length === 0}
        >
          {props.orgs.length === 0 ? <option value="">No orgs</option> : null}
          {props.orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">From</label>
        <input
          className={inputClass}
          type="date"
          value={filters.sinceDate}
          onChange={(e) => patchFilters({ sinceDate: e.target.value })}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">To</label>
        <input
          className={inputClass}
          type="date"
          value={filters.untilDate}
          onChange={(e) => patchFilters({ untilDate: e.target.value })}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Actor label</label>
        <input
          className={inputClass}
          type="text"
          placeholder="Dean"
          value={filters.actorLabelContains}
          onChange={(e) => patchFilters({ actorLabelContains: e.target.value })}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Actor user id</label>
        <input
          className={`${inputClass} font-mono text-xs`}
          type="text"
          placeholder="uuid"
          value={filters.actorUserId}
          onChange={(e) => patchFilters({ actorUserId: e.target.value })}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Domain</label>
        <select
          className={selectClass}
          value={filters.domain}
          onChange={(e) =>
            patchFilters({
              domain: e.target.value as typeof filters.domain,
            })
          }
        >
          {TIMELINE_DOMAIN_OPTIONS.map((o) => (
            <option key={o.label} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Entity type</label>
        <input
          className={inputClass}
          type="text"
          placeholder="lead, mfg_material…"
          value={filters.entityType}
          onChange={(e) => patchFilters({ entityType: e.target.value })}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Entity id</label>
        <input
          className={`${inputClass} font-mono text-xs`}
          type="text"
          placeholder="uuid drill-down"
          value={filters.entityId}
          onChange={(e) => patchFilters({ entityId: e.target.value })}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Action</label>
        <input
          className={inputClass}
          type="text"
          placeholder="sent, update…"
          value={filters.action}
          onChange={(e) => patchFilters({ action: e.target.value })}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Source</label>
        <select
          className={selectClass}
          value={filters.source}
          onChange={(e) =>
            patchFilters({
              source: e.target.value as typeof filters.source,
            })
          }
        >
          {TIMELINE_SOURCE_OPTIONS.map((o) => (
            <option key={o.label} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500">Test data</label>
        <select
          className={selectClass}
          value={filters.testData}
          onChange={(e) =>
            patchFilters({
              testData: e.target.value as typeof filters.testData,
            })
          }
        >
          {TIMELINE_TEST_DATA_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-[10rem] flex-1">
        <label className="mb-1 block text-xs text-gray-500">Search</label>
        <input
          className={`${inputClass} w-full`}
          type="search"
          placeholder="summary, customer name…"
          value={filters.q}
          onChange={(e) => patchFilters({ q: e.target.value })}
        />
      </div>
      <button
        type="button"
        className="rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900"
        onClick={props.onRefresh}
        disabled={props.working || !props.organizationId}
      >
        {props.working ? "Loading…" : "Refresh"}
      </button>
      <button
        type="button"
        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
        onClick={props.onReset}
      >
        Reset filters
      </button>
    </div>
  );
}
